// frontend/src/config/benchmarkData.ts
// Portfolio Benchmarking Data Configuration
// 
// This file contains industry and regional benchmarks for portfolio comparison.
// Update these values as new research becomes available.
//
// LAST UPDATED: December 2025
// NEXT REVIEW: June 2026
//
// ============================================================================
// HOW TO UPDATE:
// 1. Research latest data from cited sources
// 2. Update the values in this file
// 3. Update the LAST_UPDATED and source dates
// 4. Commit changes - no code changes required elsewhere
// ============================================================================

export const BENCHMARK_METADATA = {
  lastUpdated: '2025-12-18',
  nextReviewDate: '2026-06-01',
  maintainer: 'Ray',
  version: '1.2.0',
  nriVersion: 'v1.20 (December 2025)', // FEMA National Risk Index version
  notes: 'Updated to reflect FEMA NRI v1.20 (December 2025) with overhauled tsunami, inland flooding, landslide, and earthquake hazard data',
};

// ============================================================================
// INDUSTRY BENCHMARKS
// ============================================================================
// Sources:
// - FEMA National Risk Index v1.20 (December 2025): https://www.fema.gov/flood-maps/products-tools/national-risk-index
//   - Now accessed via RAPT (Resilience Analysis and Planning Tool)
//   - Key updates: Overhauled tsunami, inland flooding (pluvial + fluvial), landslide, and earthquake data
//   - Uses CDC Social Vulnerability Index for social vulnerability component
// - Moody's Climate Risk Reports (December 2025)
// - Federal Reserve Climate Scenario Analysis (2023)
// - NAIC Climate Risk Disclosure Data (2023)
// - NCREIF Property Index (December 2025)
// - Urban Land Institute Climate Risk Survey (2023)
// ============================================================================

export interface IndustryBenchmark {
  id: string;
  name: string;
  description: string;
  avgRisk: number;           // Average risk score (0-10 scale)
  avgResilience: number;     // Average community resilience (0-10 scale)
  highRiskPct: number;       // Percentage of locations with risk > 6
  geographicConcentration: number; // HHI index (0-10000, lower = more diversified)
  hazardExposure: {
    flood: number;
    wildfire: number;
    hurricane: number;
    earthquake: number;
    heat: number;
    drought: number;
  };
  typicalSize: string;
  sources: string[];
  lastUpdated: string;
}

export const INDUSTRY_BENCHMARKS: IndustryBenchmark[] = [
  {
    id: 'reit_residential',
    name: 'Residential REIT',
    description: 'Multi-family residential real estate portfolios',
    // Source: NCREIF, ULI Climate Survey 2023, FEMA NRI v1.20
    // Residential REITs tend toward sunbelt exposure (higher hurricane/heat)
    avgRisk: 4.8,
    avgResilience: 5.5,
    highRiskPct: 18,
    geographicConcentration: 2200,
    hazardExposure: {
      flood: 4.2,      // Elevated due to coastal multi-family
      wildfire: 3.1,   // Moderate - less suburban exposure
      hurricane: 4.5,  // High - FL, TX, NC concentrations
      earthquake: 2.8, // Moderate - CA exposure
      heat: 5.2,       // High - sunbelt focus
      drought: 3.8,    // Moderate
    },
    typicalSize: '$500M - $5B',
    sources: [
      'FEMA NRI v1.20 (December 2025)',
      'NCREIF Property Index Q3 2025',
      'ULI Climate Risk Survey 2023',
      'NAREIT T-Tracker Q2 2025',
    ],
    lastUpdated: '2025-12-18',
  },
  {
    id: 'reit_commercial',
    name: 'Commercial REIT',
    description: 'Office, retail, and industrial real estate',
    // Source: NCREIF, CoStar, CBRE Climate Risk Report
    // Commercial tends toward major metros with better infrastructure
    avgRisk: 4.5,
    avgResilience: 5.8,
    highRiskPct: 15,
    geographicConcentration: 2500,
    hazardExposure: {
      flood: 3.8,      // Lower - CBD locations often elevated
      wildfire: 2.5,   // Low - urban concentration
      hurricane: 4.2,  // Moderate-high - port/logistics exposure
      earthquake: 3.2, // Moderate - CA office concentration
      heat: 4.8,       // Moderate-high
      drought: 3.2,    // Moderate
    },
    typicalSize: '$1B - $10B',
    sources: [
      'NCREIF Property Index Q3 2025',
      'CBRE Climate Risk & Real Estate Report 2024',
      'CoStar Market Analytics 2024',
    ],
    lastUpdated: '2025-12-18',
  },
  {
    id: 'bank_mortgage',
    name: 'Bank Mortgage Portfolio',
    description: 'Residential and commercial mortgage holdings',
    // Source: Federal Reserve, OCC Climate Risk Reports
    // Mortgage portfolios reflect broader housing market geography
    avgRisk: 5.2,
    avgResilience: 5.2,
    highRiskPct: 22,
    geographicConcentration: 1800, // More diversified due to national reach
    hazardExposure: {
      flood: 4.8,      // High - NFIP participation gaps
      wildfire: 3.5,   // Moderate - WUI expansion
      hurricane: 5.0,  // High - FL, TX, coastal exposure
      earthquake: 2.5, // Lower - CA is smaller % of national mortgages
      heat: 5.5,       // High - population shift to sunbelt
      drought: 4.0,    // Moderate-high
    },
    typicalSize: '$10B - $100B',
    sources: [
      'Federal Reserve Climate Scenario Analysis 2023',
      'OCC Climate Risk Management Guidance 2024',
      'MBA Annual Mortgage Market Survey 2024',
    ],
    lastUpdated: '2025-12-18',
  },
  {
    id: 'insurance_property',
    name: 'P&C Insurance Book',
    description: 'Property and casualty insurance exposure',
    // Source: NAIC, AM Best, Swiss Re Sigma
    // Insurance books have adverse selection - higher risk properties seek coverage
    avgRisk: 5.5,
    avgResilience: 4.8,
    highRiskPct: 28,
    geographicConcentration: 1500, // Highly diversified by regulation
    hazardExposure: {
      flood: 5.2,      // High - NFIP write-your-own programs
      wildfire: 4.2,   // High - CA market stress
      hurricane: 5.5,  // Very high - FL, Gulf Coast
      earthquake: 3.0, // Moderate - CEA participation
      heat: 5.8,       // High - liability claims
      drought: 4.2,    // Moderate-high - ag exposure
    },
    typicalSize: '$5B - $50B',
    sources: [
      'NAIC Climate Risk Disclosure Data 2023',
      'AM Best Market Segment Report 2024',
      'Swiss Re Sigma: Natural Catastrophes 2024',
    ],
    lastUpdated: '2025-12-18',
  },
  {
    id: 'pe_real_assets',
    name: 'PE Real Assets Fund',
    description: 'Private equity real estate and infrastructure',
    // Source: Preqin, Cambridge Associates
    // PE funds can be selective, avoiding highest-risk assets
    avgRisk: 4.2,
    avgResilience: 6.2,
    highRiskPct: 12,
    geographicConcentration: 3000, // More concentrated in target markets
    hazardExposure: {
      flood: 3.5,      // Lower - due diligence screens out
      wildfire: 2.8,   // Lower - institutional quality assets
      hurricane: 3.8,  // Moderate - selective coastal exposure
      earthquake: 2.5, // Lower - seismic due diligence
      heat: 4.5,       // Moderate - growth market exposure
      drought: 3.0,    // Lower
    },
    typicalSize: '$500M - $2B',
    sources: [
      'Preqin Real Assets Report 2024',
      'Cambridge Associates PE Benchmark Q3 2025',
      'PERE Climate Risk Survey 2023',
    ],
    lastUpdated: '2025-12-18',
  },
  {
    id: 'municipal_infra',
    name: 'Municipal Infrastructure',
    description: 'City/county infrastructure and facilities',
    // Source: FEMA BRIC, ASCE Infrastructure Report Card
    // Municipal portfolios are geographically fixed
    avgRisk: 5.0,
    avgResilience: 5.0,
    highRiskPct: 20,
    geographicConcentration: 8000, // Highly concentrated by definition
    hazardExposure: {
      flood: 4.5,      // High - stormwater infrastructure
      wildfire: 3.2,   // Variable by region
      hurricane: 4.0,  // Moderate - coastal cities
      earthquake: 2.8, // Variable by region
      heat: 5.0,       // High - urban heat island
      drought: 3.5,    // Moderate - water infrastructure
    },
    typicalSize: '$100M - $1B',
    sources: [
      'FEMA BRIC Program Data 2024',
      'ASCE Infrastructure Report Card 2023',
      'NLC Municipal Climate Action Survey 2024',
    ],
    lastUpdated: '2025-12-18',
  },
];

// ============================================================================
// REGIONAL BENCHMARKS
// ============================================================================
// Sources:
// - FEMA National Risk Index v1.20 by Region (December 2025): https://www.fema.gov/flood-maps/products-tools/national-risk-index
// - NOAA Climate Normals and Regional Assessments (2020-2024)
// - Census Bureau Regional Economic Data
// - Community Disaster Resilience Zones designations (December 2024)
// ============================================================================

export interface RegionalBenchmark {
  region: string;
  states: string[];
  avgRisk: number;
  topHazards: string[];
  avgResilience: number;
  portfolioCount: string;  // Estimated institutional portfolios in region
  sources: string[];
  lastUpdated: string;
  notes: string;
}

export const REGIONAL_BENCHMARKS: RegionalBenchmark[] = [
  {
    region: 'Northeast',
    states: ['CT', 'DE', 'MA', 'MD', 'ME', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT'],
    avgRisk: 4.2,
    topHazards: ['Winter Storm', 'Coastal Flood', 'Hurricane'],
    avgResilience: 6.0,  // Higher due to infrastructure investment
    portfolioCount: '~2,500',
    sources: [
      'FEMA NRI v1.20 Northeast Region Data (December 2025)',
      'NOAA Northeast Climate Assessment 2024',
    ],
    lastUpdated: '2025-12-18',
    notes: 'Aging infrastructure but high community resilience. Nor\'easters and coastal flooding primary concerns. NRI v1.20 includes updated inland flooding with pluvial hazards.',
  },
  {
    region: 'Southeast',
    states: ['AL', 'AR', 'FL', 'GA', 'KY', 'LA', 'MS', 'NC', 'SC', 'TN', 'VA', 'WV', 'DC'],
    avgRisk: 5.8,  // Highest regional risk
    topHazards: ['Hurricane', 'Flood', 'Heat Wave'],
    avgResilience: 4.8,  // Lower due to socioeconomic factors
    portfolioCount: '~3,200',
    sources: [
      'FEMA NRI v1.20 Southeast Region Data (December 2025)',
      'NOAA Southeast Climate Assessment 2024',
      'Federal Reserve Atlanta Climate Report 2024',
    ],
    lastUpdated: '2025-12-18',
    notes: 'Highest hurricane exposure. Rapid population growth straining infrastructure. FL alone accounts for 40% of US hurricane losses. NRI v1.20 includes overhauled tsunami hazard data for coastal areas.',
  },
  {
    region: 'Midwest',
    states: ['IA', 'IL', 'IN', 'KS', 'MI', 'MN', 'MO', 'ND', 'NE', 'OH', 'SD', 'WI'],
    avgRisk: 4.5,
    topHazards: ['Tornado', 'River Flood', 'Winter Storm'],
    avgResilience: 5.5,
    portfolioCount: '~2,100',
    sources: [
      'FEMA NRI v1.20 Midwest Region Data (December 2025)',
      'NOAA Midwest Climate Assessment 2024',
    ],
    lastUpdated: '2025-12-18',
    notes: 'Tornado Alley exposure. River flooding along Mississippi/Missouri basins. Agricultural economy creates drought sensitivity. NRI v1.20 includes updated inland flooding combining fluvial and pluvial hazards.',
  },
  {
    region: 'Southwest',
    states: ['AZ', 'NM', 'OK', 'TX'],
    avgRisk: 5.2,
    topHazards: ['Heat Wave', 'Drought', 'Hurricane'],
    avgResilience: 5.0,
    portfolioCount: '~1,800',
    sources: [
      'FEMA NRI v1.20 Southwest Region Data (December 2025)',
      'NOAA Southwest Climate Assessment 2024',
    ],
    lastUpdated: '2025-12-18',
    notes: 'Extreme heat increasing. Water scarcity critical issue. TX Gulf Coast has significant hurricane exposure.',
  },
  {
    region: 'West',
    states: ['AK', 'CA', 'CO', 'HI', 'ID', 'MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
    avgRisk: 5.0,
    topHazards: ['Wildfire', 'Earthquake', 'Drought'],
    avgResilience: 5.5,
    portfolioCount: '~2,800',
    sources: [
      'FEMA NRI v1.20 West Region Data (December 2025)',
      'NOAA West Climate Assessment 2024',
      'CA Dept of Insurance Climate Report 2024',
    ],
    lastUpdated: '2025-12-18',
    notes: 'Wildfire risk increasing dramatically. CA earthquake exposure significant. Drought affecting entire region. NRI v1.20 includes new USGS high-resolution landslide data and expanded earthquake coverage to Pacific territories.',
  },
];

// ============================================================================
// PERCENTILE THRESHOLDS
// ============================================================================
// Based on distribution analysis of FEMA NRI v1.20 data across ~33,000 locations
// These define what percentile a portfolio falls into
// NRI v1.20 now uses percentile-based approach for scores (matching our methodology)
// ============================================================================

export interface PercentileThresholds {
  p10: number;  // Top 10% threshold
  p25: number;  // Top 25% threshold
  p50: number;  // Median
  p75: number;  // Bottom 25% threshold
  p90: number;  // Bottom 10% threshold
}

export const PERCENTILE_THRESHOLDS: Record<string, PercentileThresholds> = {
  // Risk Score: Lower is better
  // p10 = 3.0 means top 10% of portfolios have avg risk ≤ 3.0
  risk: { 
    p10: 3.0, 
    p25: 3.8, 
    p50: 4.8, 
    p75: 5.8, 
    p90: 6.8,
  },
  
  // Resilience: Higher is better
  // p10 = 7.0 means top 10% of portfolios have avg resilience ≥ 7.0
  resilience: { 
    p10: 3.5,  // Inverted: these are the low values
    p25: 4.5, 
    p50: 5.2, 
    p75: 6.0, 
    p90: 7.0,  // Top performers
  },
  
  // Geographic Concentration (HHI): Lower is better (more diversified)
  // p10 = 1000 means top 10% most diversified have HHI ≤ 1000
  diversification: { 
    p10: 1000, 
    p25: 1800, 
    p50: 2500, 
    p75: 4000, 
    p90: 6000,
  },
  
  // High Risk Percentage: Lower is better
  // p10 = 5 means top 10% have ≤ 5% of locations with risk > 6
  highRiskPct: { 
    p10: 5, 
    p25: 12, 
    p50: 20, 
    p75: 30, 
    p90: 45,
  },
};

// ============================================================================
// DATA SOURCES REFERENCE
// ============================================================================
// For updating benchmarks, consult these sources:
//
// FEDERAL SOURCES (Free):
// - FEMA National Risk Index: https://www.fema.gov/flood-maps/products-tools/national-risk-index
//   - Interactive Map: https://hazards.fema.gov/nri/map
//   - Data Downloads: https://hazards.fema.gov/nri/data-resources
//   - Now accessed via RAPT: Resilience Analysis and Planning Tool
// - NOAA Climate Data: https://www.ncei.noaa.gov/
// - Federal Reserve Climate Reports: https://www.federalreserve.gov/
// - OCC Climate Risk: https://www.occ.treas.gov/
// - EPA Climate Indicators: https://www.epa.gov/climate-indicators
//
// INDUSTRY SOURCES:
// - NCREIF (Real Estate): https://www.ncreif.org/
// - NAREIT (REITs): https://www.reit.com/
// - NAIC (Insurance): https://content.naic.org/
// - MBA (Mortgages): https://www.mba.org/
//
// RESEARCH/CONSULTING:
// - Swiss Re Sigma Reports: https://www.swissre.com/institute/research/sigma-research.html
// - Munich Re NatCat: https://www.munichre.com/en/solutions/for-industry-clients/natcatservice.html
// - Moody's Climate: https://www.moodys.com/climate
// - First Street Foundation: https://firststreet.org/
//
// ============================================================================

export const DATA_SOURCES = {
  federal: [
    { 
      name: 'FEMA National Risk Index (NRI)', 
      url: 'https://www.fema.gov/flood-maps/products-tools/national-risk-index', 
      updateFrequency: 'Annual',
      version: 'v1.20',
      notes: 'Now available via RAPT (Resilience Analysis and Planning Tool). Covers 18 natural hazards at county and census tract level.',
    },
    { 
      name: 'NRI Interactive Map & Data', 
      url: 'https://hazards.fema.gov/nri/', 
      updateFrequency: 'Annual',
      version: 'v1.20',
      notes: 'Direct access to NRI map, data lookup, and downloads',
    },
    { 
      name: 'NRI Data Downloads', 
      url: 'https://hazards.fema.gov/nri/data-resources', 
      updateFrequency: 'Annual',
      version: 'v1.20',
      notes: 'CSV, Shapefile, and Geodatabase formats available',
    },
    { 
      name: 'NOAA Climate Normals', 
      url: 'https://www.ncei.noaa.gov/', 
      updateFrequency: 'Decennial + Annual',
      notes: 'Climate baseline data',
    },
    { 
      name: 'Federal Reserve Climate Scenarios', 
      url: 'https://www.federalreserve.gov/publications/climate-related-risks.htm', 
      updateFrequency: 'Annual',
      notes: 'Banking sector climate risk guidance',
    },
    { 
      name: 'EPA Climate Indicators', 
      url: 'https://www.epa.gov/climate-indicators', 
      updateFrequency: 'Annual',
      notes: 'Environmental trend data',
    },
  ],
  industry: [
    { name: 'NCREIF Property Index', url: 'https://www.ncreif.org/data-products/property/', updateFrequency: 'Quarterly' },
    { name: 'NAREIT T-Tracker', url: 'https://www.reit.com/data-research/reit-indexes/reits-numbers', updateFrequency: 'Quarterly' },
    { name: 'NAIC Climate Disclosure', url: 'https://content.naic.org/cipr-topics/climate-risk', updateFrequency: 'Annual' },
    { name: 'MBA Mortgage Survey', url: 'https://www.mba.org/news-and-research/research-and-economics', updateFrequency: 'Annual' },
    { name: 'ULI Climate Risk Survey', url: 'https://knowledge.uli.org/reports/research-reports/2023/climate-risk-and-real-estate', updateFrequency: 'Annual' },
    { name: 'CBRE Climate Risk Report', url: 'https://www.cbre.com/insights/reports/global-esg-real-estate-report', updateFrequency: 'Annual' },
  ],
  research: [
    { name: 'Swiss Re Sigma', url: 'https://www.swissre.com/institute/research/sigma-research.html', updateFrequency: 'Semi-Annual' },
    { name: 'Munich Re NatCat', url: 'https://www.munichre.com/en/solutions/for-industry-clients/natcatservice.html', updateFrequency: 'Annual' },
    { name: 'First Street Foundation', url: 'https://firststreet.org/research-lab/', updateFrequency: 'Annual' },
    { name: 'Federal Reserve Atlanta Climate', url: 'https://www.atlantafed.org/economy-matters/economic-research/climate-and-the-economy', updateFrequency: 'Quarterly' },
  ],
};

// ============================================================================
// SOURCE URL MAPPING
// ============================================================================
// Direct links for specific citations used in benchmarks
// Use these to link directly to the source when displaying citations
// ============================================================================

export const SOURCE_URLS: Record<string, string> = {
  // FEMA NRI
  'FEMA NRI v1.20 (December 2025)': 'https://www.fema.gov/flood-maps/products-tools/national-risk-index',
  'FEMA NRI v1.20 Northeast Region Data (December 2025)': 'https://hazards.fema.gov/nri/data-resources',
  'FEMA NRI v1.20 Southeast Region Data (December 2025)': 'https://hazards.fema.gov/nri/data-resources',
  'FEMA NRI v1.20 Midwest Region Data (December 2025)': 'https://hazards.fema.gov/nri/data-resources',
  'FEMA NRI v1.20 Southwest Region Data (December 2025)': 'https://hazards.fema.gov/nri/data-resources',
  'FEMA NRI v1.20 West Region Data (December 2025)': 'https://hazards.fema.gov/nri/data-resources',
  
  // Industry Reports
  'NCREIF Property Index Q3 2025': 'https://www.ncreif.org/data-products/property/',
  'NAREIT T-Tracker Q2 2025': 'https://www.reit.com/data-research/reit-indexes/reits-numbers',
  'ULI Climate Risk Survey 2023': 'https://knowledge.uli.org/reports/research-reports/2023/climate-risk-and-real-estate',
  'CBRE Climate Risk & Real Estate Report (December 2025)': 'https://www.cbre.com/insights/reports/global-esg-real-estate-report',
  'CoStar Market Analytics (December 2025)': 'https://www.costar.com/products/costar-market-analytics',
  
  // Federal/Government
  'Federal Reserve Climate Scenario Analysis 2023': 'https://www.federalreserve.gov/publications/climate-related-risks.htm',
  'Federal Reserve Atlanta Climate Report (December 2025)': 'https://www.atlantafed.org/economy-matters/economic-research/climate-and-the-economy',
  'OCC Climate Risk Management Guidance (December 2025)': 'https://www.occ.treas.gov/topics/supervision-and-examination/climate/index-climate.html',
  'MBA Annual Mortgage Market Survey (December 2025)': 'https://www.mba.org/news-and-research/research-and-economics',
  
  // Insurance
  'NAIC Climate Risk Disclosure Data 2023': 'https://content.naic.org/cipr-topics/climate-risk',
  'AM Best Market Segment Report (December 2025)': 'https://www.ambest.com/review/default.aspx',
  'Swiss Re Sigma: Natural Catastrophes (December 2025)': 'https://www.swissre.com/institute/research/sigma-research.html',
  
  // Other
  'Preqin Real Assets Report (December 2025)': 'https://www.preqin.com/insights/research/reports',
  'Cambridge Associates PE Benchmark Q3 (December 2025)': 'https://www.cambridgeassociates.com/private-investment-benchmarks/',
  'PERE Climate Risk Survey 2023': 'https://www.perenews.com/',
  'FEMA BRIC Program Data (December 2025)': 'https://www.fema.gov/grants/mitigation/building-resilient-infrastructure-communities',
  'ASCE Infrastructure Report Card 2023': 'https://infrastructurereportcard.org/',
  'NLC Municipal Climate Action Survey (December 2025)': 'https://www.nlc.org/resource/local-climate-action/',
  'NOAA Northeast Climate Assessment (December 2025)': 'https://www.ncei.noaa.gov/',
  'NOAA Southeast Climate Assessment (December 2025)': 'https://www.ncei.noaa.gov/',
  'NOAA Midwest Climate Assessment (December 2025)': 'https://www.ncei.noaa.gov/',
  'NOAA Southwest Climate Assessment (December 2025)': 'https://www.ncei.noaa.gov/',
  'NOAA West Climate Assessment (December 2025)': 'https://www.ncei.noaa.gov/',
  'CA Dept of Insurance Climate Report (December 2025)': 'https://www.insurance.ca.gov/01-consumers/180-climate-change/',
};